// src/features/analytics/components/AnalyticsLayout.tsx

import { useEffect } from 'react';
import { Group, SegmentedControl, Stack, Tabs, Title } from '@mantine/core';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { analyticsRouteApi } from '../routeApi';
import { useAnalyticsMeta } from '../useAnalyticsMeta';
import { useAnalyticsScope } from '../useAnalyticsScope';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { AnalyticsErrorState } from './AnalyticsErrorState';
import { AnalyticsRangeControl } from './AnalyticsRangeControl';
import {
  TAB_LABELS,
  TAB_PATHS,
  tabFromPath,
  visibleAnalyticsTabs,
  type AnalyticsTab,
} from '../analyticsTabs';
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags';
import type { AnalyticsScopeKind } from '@/lib/constants/analytics';
import type { AnalyticsSearch } from '../searchParams';

export function AnalyticsLayout() {
  const search = analyticsRouteApi.useSearch();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { scope, networkEligible } = useAnalyticsScope(search.scope);
  const meta = useAnalyticsMeta();

  const tab = tabFromPath(pathname);
  const scopeKind = scope?.kind ?? 'account';

  // Scope-ineligible tabs bounce to the overview (audience is account-only,
  // sub-accounts is network-only; a child selection can never reach network).
  useEffect(() => {
    if (!scope) return;
    if (
      (scope.kind === 'account' && tab === 'subaccounts') ||
      (scope.kind === 'network' && tab === 'audience')
    ) {
      void navigate({ to: '/analytics', search: true, replace: true });
    }
  }, [scope, tab, navigate]);

  const metaErrorView = meta.isError ? AnalyticsUtils.errorView(meta.error) : null;
  if (metaErrorView?.kind === 'notEnabled') {
    // Expected local-dev result until production-internal enablement.
    return (
      <Stack p="md" gap="md">
        <Title order={2}>Analytics</Title>
        <AnalyticsErrorState error={meta.error} />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center" wrap="wrap">
        <Group gap="md" align="center">
          <Title order={2}>Analytics</Title>
          {networkEligible && (
            <SegmentedControl
              size="xs"
              data={[
                { value: 'account', label: 'Account' },
                { value: 'network', label: 'Network' },
              ]}
              value={scopeKind}
              onChange={(value) =>
                void navigate({
                  to: '.',
                  search: (prev) => ({
                    ...(prev as AnalyticsSearch),
                    scope: value as AnalyticsScopeKind,
                  }),
                })
              }
            />
          )}
        </Group>
        <AnalyticsRangeControl />
      </Group>

      <Tabs
        value={tab}
        onChange={(value) => {
          if (!value) return;
          void navigate({ to: TAB_PATHS[value as AnalyticsTab], search: true });
        }}
      >
        <Tabs.List>
          {visibleAnalyticsTabs(scopeKind, FEATURE_FLAGS.analyticsDeliveryEnabled).map(
            (value) => (
              <Tabs.Tab key={value} value={value}>
                {TAB_LABELS[value]}
              </Tabs.Tab>
            )
          )}
        </Tabs.List>
      </Tabs>

      <Outlet />
    </Stack>
  );
}
